import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Scan, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex flex-col justify-center px-6 py-16" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mute hover:text-slate-900 transition-colors duration-300 mb-12" data-testid="reset-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <span className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
            <Scan className="w-4.5 h-4.5 text-white" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-xl">X-GEN.AI</span>
        </div>

        {!token ? (
          <div className="border border-pulse/40 bg-pulse/5 p-8" data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-bold tracking-tight mb-3">Invalid reset link</h1>
            <p className="text-mute text-sm leading-relaxed mb-6">This link is missing its token. Request a fresh reset link.</p>
            <Link to="/forgot-password" className="font-mono text-xs uppercase tracking-[0.15em] text-brand hover:text-[#0F172A] transition-colors duration-300" data-testid="reset-request-new-link">
              Request new link
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-5 h-5 text-brand" />
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Set a new password</h1>
            </div>
            <p className="text-mute text-sm mb-10">Minimum 8 characters. The link expires 1 hour after it was sent.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="reset-password">New password</label>
                <Input id="reset-password" data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" required minLength={8} className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="reset-confirm">Confirm password</label>
                <Input id="reset-confirm" data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat the new password" required minLength={8} className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
              </div>

              {error && <p className="text-pulse text-sm font-mono" data-testid="reset-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-form-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
